import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="file-upload"
export default class extends Controller {
  static targets = [
    "dropzone",
    "input",
    "uploadZone",
    "processingAnimation",
    "resultTemplate",
    "uploadButton",
  ]
  static values = {
    processingTime: { type: Number, default: 3000 },
    spotReport: Object,
  }

  connect() {
    if (!this.hasDropzoneTarget) return console.debug("Dropzone not present")
    if (document.getElementById("spot-report-notice"))
      this.spotReportNotice = document.getElementById("spot-report-notice")

    if (this.spotReportValue.allow_upload) this.prepareUpload()
    else if (new Date() < new Date(this.spotReportValue.end_at))
      this.setupPermissionPolling()
  }

  disconnect() {
    if (this.spotReportValue.allow_upload) {
      this.removeDragDropEvents()
      this.cleanupAnimation()
    }

    this.clearTimeCheck()
  }

  prepareUpload() {
    this.setupDragDropEvents()
    this.setupRiveAnimation()
    // 只有当不在 tab 模式下，或者是 active tab 时才更新 strategy time
    if (
      !this.spotReportValue.upload_date ||
      this.spotReportValue.is_active_tab
    ) {
      this.updateStrategyTime(
        this.spotReportValue.target_date,
        this.spotReportValue.created_at
      )
    }
  }

  clearTimeCheck() {
    clearInterval(this.pollingInterval)
    clearTimeout(this.pollingTimeout)
  }

  setupPermissionPolling() {
    const now = new Date()
    const start = new Date(this.spotReportValue.start_at)
    const end = new Date(this.spotReportValue.end_at)

    console.log(
      "time window:",
      start.toLocaleTimeString(),
      end.toLocaleTimeString()
    )

    if (new Date() > end) return

    const intervalFunction = () => {
      if (new Date() > end) {
        console.log(
          "permission polling outdated, no strategy for today, timeout/interval will be clear"
        )
        this.uploadButtonTarget.innerText = "非常抱歉，今日策略异常"
        this.clearTimeCheck()
        return
      }
      this.checkPermission()
    }

    const startPolling = () => {
      this.checkPermission()
      this.pollingInterval = setInterval(intervalFunction, 10000)
    }

    if (now < start) {
      const delay = start - now
      console.log(
        `permission polling: will start polling after ${(delay / 60000).toFixed(
          2
        )} minutes`
      )

      this.pollingTimeout = setTimeout(() => {
        console.log("permission polling: timeout")
        this.checkPermission()
        this.pollingInterval = setInterval(intervalFunction, 10000)
      }, delay)
    } else if (now >= start && now < end) {
      console.log("permission polling: start")
      startPolling()
    }
  }

  get provinceName() {
    return location.pathname.split("/").filter((p) => p.length > 0)[1]
  }

  async checkPermission() {
    try {
      const response = await fetch(
        `/upload/${this.provinceName}/check_permission`,
        {
          method: "GET",
          headers: {
            "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
              .content,
          },
          credentials: "same-origin",
        }
      )

      if (!response.ok) throw new Error(`bad network ${response.status}`)

      const { allowed } = await response.json()
      if (allowed) {
        this.clearTimeCheck()
        location.reload()
      } else {
        console.log("permission polling: trying again")
      }
    } catch (error) {
      console.error(
        "Failed to check permission, please try again later.",
        error.message
      )
    }
  }

  updateButtonState() {
    const button = this.uploadButtonTarget
    button.textContent = "今日策略已就绪"
    button.classList.remove("bg-gray-400", "cursor-not-allowed")
    button.classList.add("bg-[#055FFF]")
    button.disabled = false

    // clear interval/timeout
    this.clearTimeCheck()
  }

  updateStrategyTime(targetDate, createdAt) {
    document.getElementById("strategy-time").innerHTML = this.buildStrategyTime(
      targetDate,
      createdAt
    )
  }

  setupDragDropEvents() {
    console.log("申报时间，设置拖拽区域")
    this.dragHandlers = {
      dragover: this.#dragover.bind(this),
      dragleave: this.#dragleave.bind(this),
      drop: this.#drop.bind(this),
    }

    Object.entries(this.dragHandlers).forEach(([event, handler]) =>
      this.dropzoneTarget.addEventListener(event, handler)
    )
  }

  removeDragDropEvents() {
    Object.entries(this.dragHandlers).forEach(([event, handler]) =>
      this.dropzoneTarget.removeEventListener(event, handler)
    )
  }

  setupRiveAnimation() {
    if (!this.hasProcessingAnimationTarget)
      return console.debug("ProcessingAnimation not present")

    const canvas = this.processingAnimationTarget.querySelector("canvas")
    if (!canvas) return console.error("Canvas not present")

    this.rive = new rive.Rive({
      src: canvas.dataset.riveSrc,
      canvas: canvas,
      autoplay: false,
    })
  }

  cleanupAnimation() {
    this.rive?.stop()
    this.rive?.cleanup()
  }

  openFilePicker() {
    if (this.spotReportValue.allow_upload) this.inputTarget.click()
  }

  onFileSelect(event) {
    this.processFile(event.target.files[0])
  }

  #dragover(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("scale-95", "border-[#055FFF]")
  }

  #dragleave(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("scale-95", "border-[#055FFF]")
  }

  #drop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("scale-95", "border-[#055FFF]")

    if (!this.spotReportValue.allow_upload)
      return this.showNotice("alert", "策略准备中，请稍候")

    const file = this.extractValidFile(event.dataTransfer)
    if (!file)
      return this.showNotice("alert", "未检测到文件，请在拖拽区停留足够的时间")

    this.processFile(file)
  }

  extractValidFile(dataTransfer) {
    if (dataTransfer.files?.length === 1) {
      return dataTransfer.files[0]
    }

    const items = [...dataTransfer.items]
      .filter((item) => item.kind === "file")
      .map((item) => item.getAsFile())

    return items.find((file) => file?.size > 0)
  }

  validateFile(file) {
    if (!file) {
      this.showNotice("alert", "未检测到有效文件")
      return false
    }

    if (!file.name.endsWith(".xlsx")) {
      this.showNotice("alert", "仅支持 .xlsx 格式文件")
      return false
    }

    return true
  }

  async processFile(file) {
    if (!this.validateFile(file)) return

    try {
      this.showProcessingState()
      const startTime = Date.now()

      const formData = new FormData()
      formData.set("file", file)

      // 如果是周五多日申报模式，传递 upload_date 参数
      if (this.spotReportValue.upload_date) {
        formData.set("upload_date", this.spotReportValue.upload_date)
      }

      const response = await this.submitFile(formData)
      await this.enforceProcessingDelay(startTime)

      await this.handleSuccessResponse(response, file)
    } catch (error) {
      this.handleProcessingError(error)
    }
  }

  async submitFile(formData) {
    const response = await fetch(`/upload/${this.provinceName}/process_file`, {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          .content,
      },
      credentials: "same-origin",
      body: formData,
    })
    if (!response.ok) throw new Error(await response.text())
    return response
  }

  // UI state management
  showProcessingState() {
    this.uploadZoneTarget.classList.add("hidden")
    this.processingAnimationTarget.classList.remove("hidden")
    this.rive?.play()
  }

  async enforceProcessingDelay(startTime) {
    const elapsed = Date.now() - startTime
    const remainingDelay = Math.max(3000 - elapsed, 0)
    await new Promise((resolve) => setTimeout(resolve, remainingDelay))
  }

  async handleSuccessResponse(response, originalFile) {
    const blob = await response.blob()
    const filename = `生成于${new Date().toLocaleDateString("sv-SE")}_${
      originalFile.name
    }`
    const url = URL.createObjectURL(blob)

    this.showDownloadUI(filename, url)
    this.showNotice("success", "AI 处理完成，请下载申报表")
  }

  handleProcessingError(error) {
    console.error("File processing failed:", error)
    this.resetUIState()
    this.showNotice("alert", error.message || "文件处理失败，请重试")
  }

  resetUIState() {
    this.uploadZoneTarget.classList.remove("hidden")
    this.processingAnimationTarget.classList.add("hidden")
    this.rive?.pause()
    this.removeExistingDownloadUI()
  }

  removeExistingDownloadUI() {
    const existing = this.element.querySelector(".download-container")
    existing?.remove()
  }

  // download UI
  showDownloadUI(filename, url) {
    const template = this.resultTemplateTarget.content.cloneNode(true)
    template.querySelector("#resultFilename").innerText = filename
    const downloadLink = template.querySelector("#downloadFile")
    downloadLink.href = url
    downloadLink.download = filename

    // use a wrapper
    const wrapper = document.createElement("div")
    wrapper.classList.add("download-container")
    wrapper.appendChild(template)

    this.dropzoneTarget.insertAdjacentElement("afterend", wrapper)

    this.rive?.pause()
    this.dropzoneTarget.classList.add("hidden")
    // this.processingAnimationTarget.classList.add('hidden')
  }

  showNotice(type, message) {
    if (!this.spotReportNotice) return console.error("Notice not present")

    this.spotReportNotice.innerHTML = this.buildNoticeHTML(type, message)
    this.spotReportNotice.classList.remove("hidden")

    setTimeout(() => this.spotReportNotice.classList.add("hidden"), 3000)
  }

  buildNoticeHTML(type, message) {
    const icon = {
      success: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#17C653"/>
                <path d="M16 10L11.3333 14.6667L9 12.3333" stroke="white" stroke-width="1.55556" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>`,
      alert: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 17C12.2833 17 12.521 16.904 12.713 16.712C12.905 16.52 13.0007 16.2827 13 16C12.9993 15.7173 12.9033 15.48 12.712 15.288C12.5207 15.096 12.2833 15 12 15C11.7167 15 11.4793 15.096 11.288 15.288C11.0967 15.48 11.0007 15.7173 11 16C10.9993 16.2827 11.0953 16.5203 11.288 16.713C11.4807 16.9057 11.718 17.0013 12 17ZM12 13C12.2833 13 12.521 12.904 12.713 12.712C12.905 12.52 13.0007 12.2827 13 12V8C13 7.71667 12.904 7.47933 12.712 7.288C12.52 7.09667 12.2827 7.00067 12 7C11.7173 6.99933 11.48 7.09533 11.288 7.288C11.096 7.48067 11 7.718 11 8V12C11 12.2833 11.096 12.521 11.288 12.713C11.48 12.905 11.7173 13.0007 12 13ZM12 22C10.6167 22 9.31667 21.7373 8.1 21.212C6.88334 20.6867 5.825 19.9743 4.925 19.075C4.025 18.1757 3.31267 17.1173 2.788 15.9C2.26333 14.6827 2.00067 13.3827 2 12C1.99933 10.6173 2.262 9.31733 2.788 8.1C3.314 6.88267 4.02633 5.82433 4.925 4.925C5.82367 4.02567 6.882 3.31333 8.1 2.788C9.318 2.26267 10.618 2 12 2C13.382 2 14.682 2.26267 15.9 2.788C17.118 3.31333 18.1763 4.02567 19.075 4.925C19.9737 5.82433 20.6863 6.88267 21.213 8.1C21.7397 9.31733 22.002 10.6173 22 12C21.998 13.3827 21.7353 14.6827 21.212 15.9C20.6887 17.1173 19.9763 18.1757 19.075 19.075C18.1737 19.9743 17.1153 20.687 15.9 21.213C14.6847 21.739 13.3847 22.0013 12 22Z" fill="#FF6F1E"/>
            </svg>`,
    }[type]

    return `
        <div class="flex justify-between items-center gap-2.5 h-16 px-7 py-5 bg-white rounded-xl shadow-[0px_4px_20px_0px_rgba(0,0,0,0.05)] border border-black/10">
          <div>${icon}</div>
          <div class="text-[#252f4a] text-sm font-normal font-['Inter'] leading-3">${message}</div>
        </div>
        `
  }

  buildStrategyTime(targetDate, createdAt) {
    return `
        <div class="flex justify-start items-center gap-2">
          <div class="opacity-60 justify-start text-[#252f4a] text-sm font-normal font-['Inter'] leading-3">当前策略服务日期</div>
          <div class="justify-start text-[#252f4a] text-sm font-normal font-['Inter'] leading-3">${targetDate}</div>
        </div>
        <div class="flex justify-start items-center gap-2">
          <div class="opacity-60 justify-start text-[#252f4a] text-sm font-normal font-['Inter'] leading-3">策略生成于 </div>
          <div class="justify-start text-[#252f4a] text-sm font-normal font-['Inter'] leading-3">${createdAt}</div>
        </div>
        `
  }
}
